import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import api from '../lib/api';

// v3.21-1836 · Voicemail playback through the authenticated Hub (no provider login prompt).
const fmt = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

export const playbackErrorText = async (e) => {
  const st = e?.response?.status;
  let detail = e?.response?.data?.detail;
  if (e?.response?.data instanceof Blob) {
    try { detail = JSON.parse(await e.response.data.text()).detail; } catch (_) { /* not json */ }
  }
  if (detail) return detail;
  if (st === 403) return 'You do not have access to this voicemail.';
  if (st === 404) return 'Voicemail not found.';
  return 'Playback failed. Please try again.';
};

export const VoicemailAudioPlayer = ({ voicemailId, initialDuration }) => {
  const audioRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);

  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);

  const toggle = async () => {
    if (src) {
      const a = audioRef.current;
      if (a.paused) a.play(); else a.pause();
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await api.get(`/telephony/voicemails/${voicemailId}/audio`, { responseType: 'blob' });
      setSrc(URL.createObjectURL(res.data));
    } catch (e) {
      setError(await playbackErrorText(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex items-center gap-2 min-w-[220px]" data-testid={`vm-player-${voicemailId}`}>
      <button type="button" onClick={toggle} disabled={loading}
              className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
              aria-label={playing ? 'Pause voicemail' : 'Play voicemail'}
              data-testid={`vm-play-${voicemailId}`}>
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>
      {error ? (
        <span className="text-[10px] text-red-600 flex items-center gap-1" data-testid={`vm-play-error-${voicemailId}`}>
          <AlertCircle className="h-3 w-3 shrink-0" />{error}
        </span>
      ) : (
        <>
          <input type="range" min={0} max={duration || 0} step={0.1} value={time} disabled={!src}
                 onChange={(e) => { const t = Number(e.target.value); audioRef.current.currentTime = t; setTime(t); }}
                 className="w-24 accent-blue-600" aria-label="Seek" data-testid={`vm-seek-${voicemailId}`} />
          <span className="text-[10px] tabular-nums text-slate-500" data-testid={`vm-time-${voicemailId}`}>{fmt(time)} / {fmt(duration)}</span>
        </>
      )}
      {src && (
        <audio ref={audioRef} src={src} autoPlay preload="auto" className="hidden"
               onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
               onEnded={() => { setPlaying(false); setTime(0); }}
               onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
               onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setDuration(e.currentTarget.duration); }}
               onError={() => setError('This audio file could not be played in the browser.')} />
      )}
    </div>
  );
};

export default VoicemailAudioPlayer;
